import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { StanButton, StanModal } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { EmailSubjectField } from '@/features/email-flows/components/EmailSubjectField';
import { EmailBodyField } from '@/features/email-flows/components/EmailBodyField';
import { emailBodyValidation, emailSubjectValidation } from '@/features/email-flows/validations/sharedEmailValidations';
import { useAffiliateEmailTemplate } from '../../queries/useAffiliateEmailTemplate';
import { useAffiliateActions } from '../../queries/useAffiliateActions';
import { getLocalTimezone } from '@/lib';
import { useModal } from '@/hooks/useModal';

interface AffiliateEmailModalProps {
    isOpen: boolean;
    pageId: string;
    totalAffiliates: number;
    filterArray: string;
    onClose: () => void;
    onEmailSent?: () => void;
}

const affiliateEmailFormSchema = z.object({
    subject: emailSubjectValidation,
    body: emailBodyValidation,
});

type AffiliateEmailFormData = z.infer<typeof affiliateEmailFormSchema>;

export function AffiliateEmailModal({
    isOpen,
    pageId,
    totalAffiliates,
    filterArray,
    onClose,
    onEmailSent,
}: AffiliateEmailModalProps) {
    const confirmModal = useModal();

    const { data: templateData } = useAffiliateEmailTemplate();

    const { sendMassEmail } = useAffiliateActions(pageId);

    const {
        control,
        formState: { errors, isSubmitting, isValid },
        handleSubmit,
        setValue,
        reset,
    } = useForm<AffiliateEmailFormData>({
        resolver: zodResolver(affiliateEmailFormSchema),
        mode: 'onChange',
        defaultValues: {
            subject: '',
            body: '',
        },
    });

    useEffect(() => {
        if (templateData?.default && (templateData.default.subject || templateData.default.body)) {
            reset(
                {
                    subject: templateData.default.subject,
                    body: templateData.default.body,
                },
                { keepDirty: false }
            );
        }
    }, [templateData, reset]);

    const onSubmit = useCallback(
        async (data: AffiliateEmailFormData) => {
            if (totalAffiliates === 0) {
                return;
            }

            await sendMassEmail.mutateAsync({
                params: {
                    filter_array: filterArray,
                    timezone: getLocalTimezone(),
                },
                subject: data.subject,
                body: data.body,
            });

            onEmailSent?.();
            onClose();

            // Reset form to template defaults after successful send
            if (templateData?.default) {
                reset(templateData.default, { keepDirty: false });
            }
        },
        [totalAffiliates, sendMassEmail, filterArray, onEmailSent, onClose, reset, templateData]
    );

    // Handle cancel with form reset
    const handleCancel = useCallback(() => {
        onClose();
        // Reset form to template defaults when canceling
        if (templateData?.default) {
            reset(templateData.default, { keepDirty: false });
        }
    }, [onClose, reset, templateData]);

    const isLoading = isSubmitting || sendMassEmail.isPending;

    // Handle form submission - show confirmation modal
    const handleFormSubmit = handleSubmit(() => {
        confirmModal.open();
    });

    return (
        <DropdownProvider dropdownId='affiliate-email-modal' dropdownName='affiliate-email-modal'>
            <StanModal
                id='affiliate-broadcast-email-modal'
                isOpen={isOpen}
                onClose={handleCancel}
                title='Broadcast Email'
                size='lg'
            >
                <div className='mb-6 w-full'>
                    <p className='text-gray text-label-sm-regular text-center'>
                        🚨 About to send that email to{' '}
                        <span className='text-dark font-semibold'>{totalAffiliates}</span> affiliate
                        {totalAffiliates !== 1 ? 's' : ''}? Think twice!
                    </p>
                </div>

                <form onSubmit={handleFormSubmit} className='w-full space-y-6'>
                    <EmailSubjectField
                        control={control}
                        errors={errors}
                        disabled={isLoading}
                        templateVariables={templateData?.variables}
                    />

                    <EmailBodyField
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        disabled={isLoading}
                        templateVariables={templateData?.variables}
                        templateDefaults={templateData?.default}
                    />

                    <div className='flex items-center justify-end gap-3 pt-4'>
                        <StanButton
                            name='affiliate-email-cancel-button'
                            variant='outline'
                            size='md'
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </StanButton>
                        <StanButton
                            name='affiliate-email-send-button'
                            color='primary'
                            size='md'
                            type='submit'
                            disabled={isLoading || !isValid}
                            loading={isLoading}
                        >
                            Confirm Send
                        </StanButton>
                    </div>
                </form>
            </StanModal>

            {/* Confirmation Modal */}
            <StanModal
                id='confirm-send-affiliate-email-modal'
                isOpen={confirmModal.isOpen}
                onClose={confirmModal.close}
                title='Just confirming!'
                size='sm'
                primaryActionLabel='Confirm Send'
                secondaryActionLabel='Cancel'
                onPrimaryAction={() => {
                    confirmModal.close();
                    handleSubmit(onSubmit)();
                }}
                onSecondaryAction={confirmModal.close}
                primaryActionLoading={isLoading}
                primaryActionDisabled={isLoading}
            >
                <p className='text-gray text-label-sm-regular text-center'>
                    🚨 About to send that email to <span className='text-dark font-semibold'>{totalAffiliates}</span>{' '}
                    affiliate{totalAffiliates !== 1 ? 's' : ''}? Think twice!
                </p>
            </StanModal>
        </DropdownProvider>
    );
}
